import React, { useState, useEffect } from 'react'
import {
  SIMILARITY_METHOD_OPTIONS,
  VALUATION_METHODS,
  isSimilarityMethod,
  normalizeSimilarityMethod,
} from '../../helpers/valuationMethods'
import { getCalculationCondition, getValuationConditionProfile } from '../../helpers/valuationConditions'
const API_BASE_URL = 'https://api.realitni-jestrab.cz'

const ValuationWidget = ({
  coordinates,
  layout,
  m2,
  condition,
  floor,
  hasElevator,
  underRoof,
  conditionProfile = null,
  address,
  excludedPropertyIds = [],
  comparableWeightOverrides = {},
  comparableOverrides = {},
  searchRadiusMeters,
  calculationMethod,
  onCalculationMethodChange,
  onValuationDataChange,
}) => {
  const [valuationData, setValuationData] = useState(null)
  const [calculationResponse, setCalculationResponse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!calculationResponse) return
    const requestedMethod =
      calculationMethod === VALUATION_METHODS.SIMILARITY_LEGACY ? VALUATION_METHODS.SIMILARITY_ARITHMETIC : calculationMethod
    const requestedCalculation = calculationResponse.calculations?.[requestedMethod]
    const selectedCalculation =
      requestedCalculation?.compsUsed || !calculationResponse.calculations
        ? requestedCalculation || calculationResponse
        : calculationResponse.calculations[calculationResponse.defaultMethod] || calculationResponse

    setValuationData(selectedCalculation)
    if (selectedCalculation.method !== calculationMethod && onCalculationMethodChange) {
      onCalculationMethodChange(selectedCalculation.method)
    }
    if (onValuationDataChange) {
      onValuationDataChange(selectedCalculation)
    }
  }, [calculationResponse, calculationMethod, onCalculationMethodChange, onValuationDataChange])

  // Fetch valuation data when all required fields are available
  useEffect(() => {
    // console.log('=== Valuation Widget API Trigger Check ===')
    // console.log('Coordinates:', coordinates)
    // console.log('Layout:', layout)
    // console.log('m2:', m2)
    // console.log('Condition:', condition)

    // Check if all required fields are present
    const hasCoordinates = coordinates?.lat && coordinates?.lng
    const hasLayout = layout && layout.trim() !== ''
    const hasM2 = m2 && m2.toString().trim() !== ''
    const hasCondition = condition && condition.trim() !== ''

    // console.log('Has coordinates:', hasCoordinates)
    // console.log('Has layout:', hasLayout)
    // console.log('Has m2:', hasM2)
    // console.log('Has condition:', hasCondition)

    if (!hasCoordinates || !hasLayout || !hasM2 || !hasCondition) {
      console.log('❌ Missing required fields, skipping API call')
      setCalculationResponse(null)
      setValuationData(null)
      if (onValuationDataChange) {
        onValuationDataChange(null)
      }
      return
    }

    // console.log('✅ All fields present, making API call...')

    const abortController = new AbortController()

    const fetchValuation = async () => {
      setLoading(true)
      setError(null)

      const requestBody = {
        lat: coordinates.lat,
        lon: coordinates.lng,
        layout: layout,
        m2: parseFloat(m2),
        condition: getCalculationCondition(condition),
        conditionProfile: conditionProfile || getValuationConditionProfile(condition),
        floor,
        hasElevator,
        underRoof,
        excludedPropertyIds,
        comparableWeightOverrides,
        comparableOverrides,
        searchRadiusMeters,
      }

      // console.log('📤 Sending request to /api/value with body:', requestBody)

      try {
        const response = await fetch(`${API_BASE_URL}/api/value`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: abortController.signal,
          body: JSON.stringify(requestBody),
        })

        // console.log('📥 Response status:', response.status)
        // console.log('📥 Response ok:', response.ok)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        console.log('✅ Valuation data received:', data)
        setCalculationResponse(data)
      } catch (error) {
        if (error.name === 'AbortError') return
        console.error('❌ Error fetching valuation:', error)
        setError('Chyba při načítání odhadu')
      } finally {
        if (!abortController.signal.aborted) setLoading(false)
      }
    }

    fetchValuation()
    return () => abortController.abort()
  }, [
    coordinates?.lat,
    coordinates?.lng,
    layout,
    m2,
    condition,
    conditionProfile,
    floor,
    hasElevator,
    underRoof,
    excludedPropertyIds,
    comparableWeightOverrides,
    comparableOverrides,
    searchRadiusMeters,
    onValuationDataChange,
  ])

  const handleMethodSelection = (method) => {
    const calculation = calculationResponse?.calculations?.[method]
    if (!calculation?.compsUsed) return
    onCalculationMethodChange?.(method)
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-sm sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Odhad hodnoty</h2>
        <div className="flex justify-center items-center h-32">
          <div className="text-xs sm:text-sm text-gray-500">Načítání odhadu...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-sm sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Odhad hodnoty</h2>
        <div className="bg-red-50 border border-red-200 text-xs sm:text-sm text-red-700 px-4 py-3 rounded-lg">{error}</div>
      </div>
    )
  }

  if (!valuationData) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-sm sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Odhad hodnoty</h2>
        <div className="text-xs sm:text-sm text-gray-500 text-center py-8">Vyplňte adresu, dispozici, velikost a stav pro zobrazení odhadu</div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h2 className="text-sm sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Odhad hodnoty</h2>
      {calculationResponse?.calculations && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {SIMILARITY_METHOD_OPTIONS.map((option) => {
            const calculation = calculationResponse.calculations[option.id]
            const selected = isSimilarityMethod(valuationData.method) && normalizeSimilarityMethod(valuationData.method) === option.id
            return (
              <button
                key={option.id}
                type="button"
                disabled={!calculation?.compsUsed}
                onClick={() => handleMethodSelection(option.id)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <div className="text-xs font-semibold text-gray-900">{option.label}</div>
                <div className="mt-1 text-[10px] text-gray-500">{option.description}</div>
                <div className="mt-1 text-sm font-bold text-gray-900">
                  {calculation?.compsUsed ? `${Math.round(calculation.anchorPrice).toLocaleString()} Kč` : 'Nedostatek srovnání'}
                </div>
                <div className="mt-1 text-xs text-gray-500">{calculation?.compsUsed || 0} použitých nabídek</div>
              </button>
            )
          })}
        </div>
      )}
      {valuationData.method === VALUATION_METHODS.LADDER && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          Historický odhad používá žebříčkovou metodu. Vyberte jednu z nových metod pro explicitní převod.
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="text-center">
          <div className="text-base sm:text-2xl font-bold text-green-600">{valuationData.anchorPrice?.toLocaleString() || 'N/A'}</div>
          <div className="text-xs sm:text-sm text-gray-600">Odhadovaná hodnota (Kč)</div>
        </div>
        <div className="text-center">
          <div className="text-base sm:text-2xl font-bold text-blue-600">{valuationData.anchorPpm2?.toLocaleString() || 'N/A'}</div>
          <div className="text-xs sm:text-sm text-gray-600">Cena za m² (Kč)</div>
        </div>
        <div className="text-center">
          <div className="text-base sm:text-2xl font-bold text-orange-600">{valuationData.confidence ? Math.round(valuationData.confidence * 100) : 'N/A'}%</div>
          <div className="text-xs sm:text-sm text-gray-600">Důvěra</div>
        </div>
        <div className="text-center">
          <div className="text-base sm:text-2xl font-bold text-purple-600">{valuationData.compsUsed || 'N/A'}</div>
          <div className="text-xs sm:text-sm text-gray-600">Srovnatelné nemovitosti</div>
        </div>
      </div>
      {valuationData.method && (
        <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
          <div className="text-xs sm:text-sm text-gray-600">Vybraná metoda: {valuationData.methodLabel || valuationData.method}</div>
        </div>
      )}
    </div>
  )
}

export default ValuationWidget
